import React, { useMemo, useState } from 'react';
import { Notice } from '../types/notice';
import NoticeDetailModal from './NoticeDetailModal';

interface NoticeListProps {
  notices: Notice[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const NoticeList: React.FC<NoticeListProps> = ({ notices, currentPage, totalPages, onPageChange }) => {
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleNoticeClick = (notice: Notice, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('Notice card clicked:', notice.title);
    setSelectedNotice(notice);
    setShowModal(true);
    console.log('Modal state set - showModal:', true, 'selectedNotice:', notice.title);
  };

  const handleCloseModal = () => {
    console.log('Closing modal');
    setShowModal(false);
    setSelectedNotice(null);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="notice-list-container">
      <div className="row g-3 mb-4">
        {notices.map((notice) => (
          <div key={notice.id} className="col-12 col-md-6">
            <div 
              className="notice-item card card-surface h-100"
              onClick={(e) => handleNoticeClick(notice, e)}
              style={{ 
                cursor: 'pointer', 
                transition: 'all 0.2s ease', 
                display: 'flex', 
                flexDirection: 'column',
                pointerEvents: 'auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <div className="card-body p-3 d-flex flex-column h-100">
                <div className="d-flex gap-3 align-items-start flex-grow-1">
                  <div className="notice-date-block flex-shrink-0">
                    <div className="notice-date-day">
                      {new Date(notice.date).getDate()}
                    </div>
                    <div className="notice-date-month-year">
                      {new Date(notice.date).toLocaleString('en-US', { month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div className="flex-grow-1 min-w-0 d-flex align-items-center">
                    <h6 className="notice-title mb-0" style={{ 
                      wordBreak: 'break-word', 
                      lineHeight: '1.4', 
                      overflowWrap: 'break-word'
                    }}>
                      {notice.title}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="notice-pagination">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            {/* Left pagination - Numbered pages */}
            <nav aria-label="Notice pagination">
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                    disabled={currentPage === 1}
                    aria-label="First page"
                  >
                    &laquo;
                  </button>
                </li>
                {pageNumbers.map((page, index) => (
                  <li key={index} className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                    {page === '...' ? (
                      <span className="page-link">...</span>
                    ) : (
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageClick(page as number)}
                      >
                        {page}
                      </button>
                    )}
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                    disabled={currentPage === totalPages}
                    aria-label="Last page"
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>

            {/* Right pagination - Previous/Next */}
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handlePrevious}
                disabled={currentPage === 1}
              >
                &lt; Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next &gt;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notice Detail Modal */}
      <NoticeDetailModal
        show={showModal}
        notice={selectedNotice}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default NoticeList;
